import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BTN_XS } from '../constants/buttons';
import { NAV_CLEAR_BOTTOM, CONSENT_BANNER_CLEAR_VAR } from '../constants/overlayLayers';

/** 🍪 R-2: while the banner shows, publish its TOP edge (+8px, measured from the viewport bottom) so
 *  the phone bottom toast (`TOAST_PHONE_BOTTOM_HOST`) sits above it, not over it. From layout
 *  (`bottom` + `offsetHeight`), never `getBoundingClientRect`: the slide-in `translate` would make
 *  the first reading wrong. Removed the moment the banner goes. */
export function consentBannerClearance(el) {
  const bottom = parseFloat(window.getComputedStyle(el).bottom) || 0;
  return `${Math.ceil(bottom + el.offsetHeight + 8)}px`;
}

const STORAGE_KEY = 'sfx_cookie_consent';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!visible || !el) return undefined;
    const publish = () => root.style.setProperty(CONSENT_BANNER_CLEAR_VAR, consentBannerClearance(el));
    publish();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', publish);
      root.style.removeProperty(CONSENT_BANNER_CLEAR_VAR);
    };
  }, [visible]);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) !== 'accepted') {
      // Small delay so the slide-up animation is visible on first paint
      const t = setTimeout(() => setVisible(true), 100);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setVisible(false);
  };

  // Already accepted — render nothing
  if (localStorage.getItem(STORAGE_KEY) === 'accepted') return null;

  return (
    <div
      /* 📱 Below `md` (mobile batch item 25) it covered the bottom nav: it now
         floats as a card with side gutters, above the nav and the safe area,
         and slides off past the screen edge (`translate-y-[200%]`) rather than
         by its own height, which would leave it parked over the nav. */
      ref={ref}
      data-testid="cookie-consent-banner"
      className={`fixed bottom-0 left-0 right-0 z-50 bg-gray-900 border-t border-gray-700
        max-md:left-2 max-md:right-2 ${NAV_CLEAR_BOTTOM} max-md:rounded-lg max-md:border max-md:shadow-lg
        transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full max-md:translate-y-[200%]'}`}
    >
      <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
        <p className="text-gray-300 text-xs text-center sm:text-left leading-relaxed">
          This site uses essential cookies for authentication and localStorage for your
          preferences. No tracking or advertising cookies are used.{' '}
          <Link
            to="/privacy-policy"
            className="underline text-blue-400 hover:text-blue-300"
          >
            Learn more
          </Link>
        </p>
        <button
          onClick={handleAccept}
          className={`${BTN_XS.primary} whitespace-nowrap shrink-0 max-md:min-h-[44px] max-md:px-6`}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
